import styled, { css } from 'styled-components'

import { EDITOR_ACTIVE_BORDER_WIDTH } from '@messenger/consts'

import { colors } from '@/utils/styling/colors'

/**
 * The chrome of a classic (TinyMCE-era) item: text, image, line, QR code,
 * watermark. It is identical to staging's chrome: the classic items change
 * nothing until they are replaced by editor text boxes. The editor items
 * draw their own chrome from `ItemControlOverlay.styled.ts`. Deleting the
 * classic items deletes this file.
 */

// =============================================================================
// SELECTION BORDER
// =============================================================================

type SelectionBorderProps = {
  $convertedToEditable?: boolean
}

export const ClassicSelectionBorder = styled.div<SelectionBorderProps>(
  ({ $convertedToEditable }) => css`
    position: absolute;
    inset: 0;
    outline: ${EDITOR_ACTIVE_BORDER_WIDTH}px solid;
    outline-color: ${$convertedToEditable
      ? colors('orange')
      : colors('brand', 75)};
    pointer-events: none;
    transition:
      outline 0.1s ease-in-out,
      outline-offset 0.1s ease-in-out;
  `,
)

// =============================================================================
// CONTROL TOOLBAR (horizontal bar above item)
// =============================================================================

type ControlToolbarProps = {
  $zoom: number
}

/**
 * The toolbar icons read their size from the `--overlay-icon-*` variables
 * (see `iconBase` in `ItemControlOverlay.styled.ts`); the classic bar sets
 * them to the classic 20px icon with a 2px pad.
 */
export const ClassicControlToolbar = styled.div<ControlToolbarProps>(
  ({ $zoom }) => {
    // Inverse scale to counteract page zoom, clamped to prevent extremes
    const scale = Math.min(2, Math.max(0.5, 100 / $zoom))
    const gapScale = Math.min(2, Math.max(0.6, 100 / $zoom))
    return css`
      && {
        --overlay-icon-size: 20px;
        --overlay-icon-padding: 2px;
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%) translateY(calc(-100% - ${6 * gapScale}px))
          scale(${scale});
        transform-origin: bottom center;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        background-color: #ffffff;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        pointer-events: auto;
        white-space: nowrap;
        z-index: 1;
      }
    `
  },
)

// =============================================================================
// WARNING BAR (vertical stack on right side)
// =============================================================================

type WarningBarProps = {
  $zoom: number
}

export const ClassicWarningBar = styled.div<WarningBarProps>(({ $zoom }) => {
  // Inverse scale to counteract page zoom, clamped to prevent extremes
  const scale = Math.min(2, Math.max(0.5, 100 / $zoom))
  return css`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    pointer-events: auto;
    transform: translate(${-5 * scale}px, ${5 * scale}px) scale(${scale});
    transform-origin: top right;
  `
})

// =============================================================================
// RESIZE HANDLE (stays at bottom-right corner)
// Scaling is applied via wrapper div in ClassicControls, not here
// =============================================================================

type ResizeHandleProps = {
  $cursor: string
}

export const ClassicResizeHandle = styled.div<ResizeHandleProps>(
  ({ $cursor }) => css`
    width: 24px;
    height: 24px;
    border-bottom: solid 10px ${colors('brand', 75)};
    border-right: solid 10px ${colors('brand', 75)};
    border-left: solid 10px transparent;
    border-top: solid 10px transparent;
    cursor: ${$cursor}-resize;
    pointer-events: auto;
  `,
)

// =============================================================================
// EDGE RESIZE HANDLES (for single-axis resize - images only)
// =============================================================================

const edgeHandleBase = css`
  position: absolute;
  background-color: ${colors('brand', 75)};
  border-radius: 3px;
  pointer-events: auto;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors('brand')};
  }
`

/**
 * Right edge handle - drag horizontally to change width only
 */
export const ClassicRightEdgeHandle = styled.div`
  ${edgeHandleBase}
  width: 8px;
  height: 32px;
  right: -4px;
  top: 50%;
  transform: translateY(-50%);
  cursor: ew-resize;
`

type BottomEdgeHandleProps = {
  $disabled?: boolean
}

/**
 * Bottom edge handle - drag vertically to change height only
 * Disabled when height auto-adjusts (maintainAspectRatio is OFF)
 */
export const ClassicBottomEdgeHandle = styled.div<BottomEdgeHandleProps>(
  ({ $disabled }) => css`
    ${edgeHandleBase}
    width: 32px;
    height: 8px;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    cursor: ${$disabled ? 'not-allowed' : 'ns-resize'};
    opacity: ${$disabled ? 0.4 : 1};

    &:hover {
      background-color: ${$disabled ? colors('brand', 75) : colors('brand')};
    }
  `,
)
